import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiUpload, FiTrash2, FiImage, FiShield, FiKey, FiPlus, FiCopy, FiCheck, FiAlertTriangle } from 'react-icons/fi';

async function getCroppedImg(imageSrc, crop) {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 50;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    crop.x, crop.y, crop.width, crop.height,
    0, 0, 200, 50
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(new File([blob], 'logo.png', { type: 'image/png' }));
    }, 'image/png');
  });
}

export default function Settings() {
  const { tenant, user, setTenant } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Crop state
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const fileInputRef = useRef(null);

  const isOwnerOrAdmin = user?.role === 'OWNER' || user?.role === 'SUPER_ADMIN';
  const canManageApiKeys = ['OWNER', 'MANAGER', 'SUPER_ADMIN'].includes(user?.role);

  // ── API Keys state ──
  const [apiKeys, setApiKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    if (!tenant || !canManageApiKeys) return;
    setKeysLoading(true);
    try {
      const data = await api.getTenantApiKeys(tenant.id);
      setApiKeys(data);
    } catch (err) {
      console.error('Fetch api keys error:', err);
    } finally {
      setKeysLoading(false);
    }
  }, [tenant, canManageApiKeys]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    setCreatingKey(true);
    try {
      // Pass tenant.id explicitly — required when caller is SUPER_ADMIN
      // (impersonation is client-side, so the DB can't infer it).
      const result = await api.createTenantApiKey(newKeyName.trim(), tenant?.id || null);
      setRevealedKey(result);
      setNewKeyName('');
      setShowNewKeyModal(false);
      await fetchApiKeys();
    } catch (err) {
      alert(`Error creating API key: ${err.message || err}`);
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async (key) => {
    if (!confirm(`Revoke API key "${key.name}"? Any WordPress site using this key will stop working immediately.`)) return;
    try {
      await api.revokeTenantApiKey(key.id);
      await fetchApiKeys();
    } catch (err) {
      alert(`Error revoking key: ${err.message || err}`);
    }
  };

  const handleCopyKey = async () => {
    if (!revealedKey?.plaintext_key) return;
    try {
      await navigator.clipboard.writeText(revealedKey.plaintext_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      // ignore
    }
  };

  const formatKeyDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSaveCropped = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setUploading(true);
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      const logoUrl = await api.uploadTenantLogo(tenant.id, croppedFile);
      // Update tenant in context so sidebar reflects immediately
      if (typeof setTenant === 'function') {
        setTenant({ ...tenant, logo_url: logoUrl });
      }
      setImageSrc(null);
    } catch (err) {
      console.error('Logo upload error:', err);
      alert(`Error uploading logo: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSkipCrop = async () => {
    if (!imageSrc) return;
    setUploading(true);
    try {
      // Convert the original image data URL to a file, then resize to 200x50
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = imageSrc;
      });

      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');

      // Fit the image into 200x50 maintaining aspect ratio, centered
      const scale = Math.min(200 / image.width, 50 / image.height);
      const w = image.width * scale;
      const h = image.height * scale;
      ctx.drawImage(image, (200 - w) / 2, (50 - h) / 2, w, h);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'logo.png', { type: 'image/png' });

      const logoUrl = await api.uploadTenantLogo(tenant.id, file);
      if (typeof setTenant === 'function') {
        setTenant({ ...tenant, logo_url: logoUrl });
      }
      setImageSrc(null);
    } catch (err) {
      console.error('Logo upload error:', err);
      alert(`Error uploading logo: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!confirm('Remove the custom logo? The default FFL A&D Book logo will be used.')) return;
    setRemoving(true);
    try {
      await api.removeTenantLogo(tenant.id);
      if (typeof setTenant === 'function') {
        setTenant({ ...tenant, logo_url: null });
      }
    } catch (err) {
      console.error('Logo remove error:', err);
      alert(`Error removing logo: ${err.message}`);
    } finally {
      setRemoving(false);
    }
  };

  if (!tenant) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-slate-500">No company assigned. Settings are not available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Settings</h2>
        <p className="text-sm text-slate-500">Manage your company branding and preferences.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company Logo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Upload a logo to replace the default FFL A&D Book branding in the sidebar. Recommended size: 200px x 50px.
            </p>

            {/* Current logo preview */}
            <div className="flex items-center gap-4">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Current:</div>
              <div className="flex items-center h-[50px] w-[200px] bg-slate-100 dark:bg-slate-800 rounded-md overflow-hidden border border-slate-200 dark:border-slate-700">
                {tenant.logo_url ? (
                  <img
                    src={tenant.logo_url}
                    alt="Company logo"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex items-center gap-2 px-3">
                    <SafeIcon icon={FiShield} className="h-6 w-6 text-primary-600" />
                    <span className="text-sm font-bold text-slate-900 dark:text-white">FFL A&D Book</span>
                  </div>
                )}
              </div>
              {tenant.logo_url && isOwnerOrAdmin && (
                <Button
                  variant="secondary"
                  onClick={handleRemoveLogo}
                  isLoading={removing}
                  icon={FiTrash2}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              )}
            </div>

            {/* Upload button */}
            {isOwnerOrAdmin && !imageSrc && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  icon={FiUpload}
                >
                  Upload New Logo
                </Button>
              </div>
            )}

            {!isOwnerOrAdmin && (
              <p className="text-sm text-slate-400 italic">Only the Owner can change the logo.</p>
            )}

            {/* Crop modal */}
            {imageSrc && (
              <div className="space-y-4">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Crop your logo to fit (200 x 50 pixels):
                </div>
                <div className="relative w-full h-64 bg-slate-900 rounded-lg overflow-hidden">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={200 / 50}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    cropShape="rect"
                    showGrid={false}
                    style={{
                      containerStyle: { borderRadius: '8px' },
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs text-slate-500 shrink-0">Zoom:</label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 accent-primary-600"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveCropped} isLoading={uploading} icon={FiImage}>
                    Save Cropped Logo
                  </Button>
                  <Button variant="secondary" onClick={handleSkipCrop} isLoading={uploading}>
                    Skip Cropping
                  </Button>
                  <Button variant="secondary" onClick={() => setImageSrc(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─────────────── API Keys (WooCommerce / external integrations) ─────────────── */}
      {canManageApiKeys && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>API Keys</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Use these keys to let your WordPress / WooCommerce site send incoming order
                  notices to your FFL bound book. Keys are tenant-scoped and can be revoked at
                  any time.
                </p>
              </div>
              <Button
                icon={FiPlus}
                onClick={() => { setShowNewKeyModal(true); setNewKeyName(''); }}
              >
                Generate New Key
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {keysLoading ? (
              <div className="text-sm text-slate-500">Loading keys…</div>
            ) : apiKeys.length === 0 ? (
              <div className="text-sm text-slate-500 italic">
                No API keys yet. Generate one to connect your WooCommerce site.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Key</th>
                      <th className="px-3 py-2">Last Used</th>
                      <th className="px-3 py-2">Created</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                    {apiKeys.map((k) => (
                      <tr key={k.id} className={k.revoked_at ? 'opacity-50' : ''}>
                        <td className="px-3 py-2 font-medium">{k.name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}…</td>
                        <td className="px-3 py-2 text-slate-500">{formatKeyDate(k.last_used_at)}</td>
                        <td className="px-3 py-2 text-slate-500">{formatKeyDate(k.created_at)}</td>
                        <td className="px-3 py-2">
                          {k.revoked_at ? (
                            <Badge variant="error">Revoked</Badge>
                          ) : (
                            <Badge variant="success">Active</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!k.revoked_at && (
                            <button
                              type="button"
                              onClick={() => handleRevokeKey(k)}
                              className="text-xs font-medium text-red-600 hover:text-red-700"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Generate-key modal ── */}
      {showNewKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Generate New API Key
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Give this key a recognizable name (e.g. the WordPress site that will use it).
            </p>
            <Input
              label="Key Name"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="WooCommerce - rifleworks.com"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && newKeyName.trim()) handleCreateKey(); }}
            />
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowNewKeyModal(false)}>Cancel</Button>
              <Button onClick={handleCreateKey} isLoading={creatingKey} disabled={!newKeyName.trim()}>
                Generate Key
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reveal-key-once modal ── */}
      {revealedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="flex items-start gap-3 mb-4">
              <SafeIcon icon={FiKey} className="h-6 w-6 text-primary-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Your new API key
                </h3>
                <p className="text-sm text-slate-500">
                  This is the only time the full key will be displayed. Copy it now.
                </p>
              </div>
            </div>

            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 mb-4 dark:bg-yellow-900/20 dark:border-yellow-800">
              <div className="flex items-start gap-2">
                <SafeIcon icon={FiAlertTriangle} className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  We never store the plaintext key — only a hash. If you lose it, you'll need to
                  generate a new one and update your WordPress site.
                </p>
              </div>
            </div>

            <div className="rounded-md bg-slate-100 p-3 dark:bg-slate-800 mb-4">
              <div className="text-xs font-semibold text-slate-500 mb-1">Name: {revealedKey.name}</div>
              <div className="font-mono text-sm break-all text-slate-900 dark:text-white">
                {revealedKey.plaintext_key}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" icon={copiedKey ? FiCheck : FiCopy} onClick={handleCopyKey}>
                {copiedKey ? 'Copied!' : 'Copy Key'}
              </Button>
              <Button onClick={() => setRevealedKey(null)}>I've Saved It</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
